/*
  bs-droste.js — the "Dig deeper" tunnel banner (sections/bs-droste.liquid).

  Each frame's card holds a smaller, tilted copy of the frame. That placement
  (scale --s, tilt --rot, centre --cx/--cy) is a spiral similarity, so it has
  exactly one fixed point. Scaling the camera by s^-f and turning it by -f·rot
  about that point slides frame n onto frame n+1 as f runs from 0 to 1. At 1
  we snap back to 0 and move every frame's colour tone along by one, which
  looks the same, so the dive can go on forever with the camera never zoomed
  past one level.

  Motion modes (section setting, read from data-motion):
    autoplay  hold, dive one full colour cycle, land, repeat while on screen
    scroll    dive depth follows the banner's position in the viewport
    off       still panels
  Visitors who ask for reduced motion always get the still panels.
*/
(() => {
  if (customElements.get('bs-droste')) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const TONES = 3; // colours in the cycle, matching the three colour settings
  const DIVE_LEVELS = TONES; // one dive passes every colour once
  const DIVE_MS = 2400;

  const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

  class BsDroste extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('.bs-droste__stage');
      this.camera = this.querySelector('.bs-droste__camera');
      this.frames = [...this.querySelectorAll('.bs-droste__frame')];
      this.heading = this.querySelector('.bs-droste__frame .bs-droste__heading');
      this.toggle = this.querySelector('.bs-droste__toggle');
      if (!this.stage || !this.camera || !this.frames.length) return;

      this.mode = this.dataset.motion;
      this.holdMs = (parseFloat(this.dataset.hold) || 2.5) * 1000;
      this.shift = 0;
      this.depth = 0;
      this.visible = false;
      this.userPaused = false;

      this.measure();
      this.resizer = new ResizeObserver(() => this.measure());
      this.resizer.observe(this.stage);

      this.onMotionPref = () => this.setup();
      reduceMotion.addEventListener('change', this.onMotionPref);
      this.setup();
    }

    disconnectedCallback() {
      this.teardown();
      this.resizer?.disconnect();
      reduceMotion.removeEventListener('change', this.onMotionPref);
    }

    setup() {
      this.teardown();
      this.render(0);
      if (reduceMotion.matches || (this.mode !== 'autoplay' && this.mode !== 'scroll')) return;

      this.watcher = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        if (this.mode === 'autoplay') this.visible ? this.play() : this.stop();
      });
      this.watcher.observe(this.stage);

      if (this.mode === 'autoplay') {
        // Start partway through the first hold so the dive comes soon after
        // the banner scrolls into view.
        this.clock = Math.max(0, this.holdMs - 900);
        if (this.toggle) {
          this.toggle.hidden = false;
          this.onToggle = () => this.setPaused(!this.userPaused);
          this.toggle.addEventListener('click', this.onToggle);
        }
      } else {
        this.onScroll = () => {
          if (!this.visible || this.scrollFrame) return;
          this.scrollFrame = requestAnimationFrame(() => {
            this.scrollFrame = null;
            this.followScroll();
          });
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.followScroll();
      }
    }

    teardown() {
      this.stop();
      this.watcher?.disconnect();
      this.watcher = null;
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      this.onScroll = null;
      if (this.toggle && this.onToggle) this.toggle.removeEventListener('click', this.onToggle);
      if (this.toggle) this.toggle.hidden = true;
      this.removeAttribute('data-paused');
      this.userPaused = false;
    }

    // Find the camera's fixed point from the card placement in bs-droste.css.
    measure() {
      const style = getComputedStyle(this.stage);
      const s = parseFloat(style.getPropertyValue('--s'));
      const rot = parseFloat(style.getPropertyValue('--rot'));
      const cx = parseFloat(style.getPropertyValue('--cx'));
      const cy = parseFloat(style.getPropertyValue('--cy'));
      const w = this.stage.clientWidth;
      const h = this.stage.clientHeight;
      if (!s || !w || !h) return;

      // The card maps a point u (relative to the frame centre) to c + s·R·u,
      // where c is the card centre. Its fixed point p solves p = c + s·R·p.
      const theta = (rot * Math.PI) / 180;
      const a = s * Math.cos(theta);
      const b = s * Math.sin(theta);
      const ox = (cx - 0.5) * w;
      const oy = (cy - 0.5) * h;
      const det = (1 - a) * (1 - a) + b * b;
      const px = w / 2 + ((1 - a) * ox - b * oy) / det;
      const py = h / 2 + (b * ox + (1 - a) * oy) / det;

      this.geo = { s, rot };
      this.camera.style.transformOrigin = `${px}px ${py}px`;
      this.render(this.depth, true);
    }

    // depth k: whole levels shift the colour tones, the fraction moves the camera.
    render(k, force) {
      if (!this.geo) return;
      if (k === this.depth && !force) return;
      this.depth = k;

      const whole = Math.floor(k);
      const f = k - whole;
      const shift = ((whole % TONES) + TONES) % TONES;
      if (shift !== this.shift || force) {
        this.shift = shift;
        this.frames.forEach((frame, i) => {
          frame.dataset.tone = (i + shift) % TONES;
        });
      }

      const { s, rot } = this.geo;
      this.camera.style.transform = f ? `rotate(${-f * rot}deg) scale(${Math.pow(s, -f)})` : '';
    }

    /* ---------- autoplay ---------- */

    play() {
      if (this.frame || this.userPaused || !this.visible) return;
      this.last = performance.now();
      this.frame = requestAnimationFrame((now) => this.tick(now));
    }

    stop() {
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
      this.camera?.classList.remove('is-diving');
      if (this.camera) this.camera.style.filter = '';
    }

    setPaused(paused) {
      this.userPaused = paused;
      this.toggleAttribute('data-paused', paused);
      this.toggle.setAttribute('aria-label', paused ? 'Play animation' : 'Pause animation');
      paused ? this.stop() : this.play();
    }

    tick(now) {
      // Cap the step so a backgrounded tab doesn't jump the timeline.
      const dt = Math.min(now - this.last, 50);
      this.last = now;
      this.clock += dt;

      const cycle = this.holdMs + DIVE_MS;
      const t = this.clock % cycle;
      const diving = t >= this.holdMs;
      const k = diving ? DIVE_LEVELS * easeInOutCubic((t - this.holdMs) / DIVE_MS) : 0;

      if (diving) {
        // A touch of motion blur at full speed, like the camera is moving.
        // Speed is in levels per second; the dive peaks just under 4.
        const speed = Math.abs(k - (this.prevK || 0)) / (dt / 1000 || 1);
        const blur = Math.min(2.2, Math.max(0, speed - 1.2) * 1.1);
        this.camera.style.filter = blur > 0.25 ? `blur(${blur.toFixed(2)}px)` : '';
        this.camera.classList.add('is-diving');
      } else if (this.wasDiving) {
        this.camera.style.filter = '';
        this.camera.classList.remove('is-diving');
        this.land();
      }
      this.wasDiving = diving;
      this.prevK = k;
      this.render(k % TONES);

      this.frame = requestAnimationFrame((next) => this.tick(next));
    }

    // A comic-book thunk on the headline when the dive lands. The easing sits
    // on each keyframe: an overshoot easing on the whole effect would rush
    // through the swell and hide it.
    land() {
      this.heading?.animate(
        [
          { transform: 'scale(1) rotate(0deg)', easing: 'ease-out' },
          { transform: 'scale(1.07) rotate(-1.5deg)', offset: 0.35, easing: 'cubic-bezier(0.3, 1.6, 0.5, 1)' },
          { transform: 'scale(1) rotate(0deg)' },
        ],
        { duration: 480 }
      );
    }

    /* ---------- scroll ---------- */

    followScroll() {
      const rect = this.stage.getBoundingClientRect();
      const vh = window.innerHeight;
      // 0 as the banner's top enters at the bottom, 1 as its bottom leaves the top.
      const progress = Math.min(1, Math.max(0, (vh - rect.top) / (vh + rect.height)));
      this.render(DIVE_LEVELS * progress);
    }
  }

  customElements.define('bs-droste', BsDroste);
})();
